import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ConsoleError, type SessionState } from '@/api'
import { keys, useApi } from '@/api/hooks'
import { copy } from '@/copy'
import { Mark } from '@/components/Mark'
import { serverNow, useConsole } from '@/lib/store'
import { useTitle } from '@/lib/useTitle'

type Step = Extract<SessionState, { state: 'signed_out' } | { state: 'code' }>

// Sign in (D-014, D-044, D-045): an email, then the 6-digit code we email,
// the CLI's codes and limits. The first sign-in creates the account.
export function SignIn({ session }: { session: Step }) {
  useTitle(copy.titles.signIn)
  const api = useApi()
  const client = useQueryClient()
  const setServerNow = useConsole((s) => s.setServerNow)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [problem, setProblem] = useState<{ text: string; field: boolean } | null>(null)
  const [busy, setBusy] = useState(false)
  const field = useRef<HTMLInputElement>(null)

  const coding = session.state === 'code'
  useEffect(() => {
    field.current?.focus()
  }, [coding])

  const settle = (next: SessionState) => {
    setServerNow(next.now)
    client.setQueryData(keys.session, next)
  }

  const fail = (e: unknown) => {
    if (!(e instanceof ConsoleError)) return setProblem({ text: copy.unreachable, field: false })
    switch (e.code) {
      case 'invalid_email':
        return setProblem({ text: copy.signIn.invalidEmail, field: true })
      case 'code_length':
        return setProblem({ text: copy.signIn.codeLength, field: true })
      case 'wrong_code':
        return setProblem({ text: copy.signIn.wrongCode(e.attemptsLeft), field: true })
      case 'too_many_codes':
        return setProblem({ text: copy.signIn.tooMany(e.email || email.trim()), field: false })
      case 'wait':
        return setProblem({ text: copy.signIn.wait, field: false })
      case 'expired':
      case 'no_more_codes':
        // The account host has already forgotten the challenge; so do we.
        setCode('')
        client.setQueryData<SessionState>(keys.session, (s) => (s ? { state: 'signed_out', csrf: s.csrf, now: s.now } : s))
        return setProblem({ text: e.code === 'expired' ? copy.signIn.expired : copy.signIn.noMoreCodes, field: false })
      case 'forbidden':
        // A token that no longer matches: ask again for a fresh one.
        void client.invalidateQueries({ queryKey: keys.session })
        return setProblem({ text: copy.unreachable, field: false })
      default:
        return setProblem({ text: copy.unreachable, field: false })
    }
  }

  const run = async (work: () => Promise<SessionState>) => {
    if (busy) return
    setBusy(true)
    setProblem(null)
    try {
      settle(await work())
    } catch (e) {
      fail(e)
    } finally {
      setBusy(false)
    }
  }

  const submitEmail = (e: FormEvent) => {
    e.preventDefault()
    void run(() => api.startSignIn(session.csrf, email.trim()))
  }
  const submitCode = (e: FormEvent) => {
    e.preventDefault()
    void run(() => api.verifySignIn(session.csrf, code))
  }
  const again = () => void run(() => api.resendSignIn(session.csrf))
  const restart = () => {
    setProblem(null)
    setCode('')
    client.setQueryData<SessionState>(keys.session, { state: 'signed_out', csrf: session.csrf, now: session.now })
  }

  const errorId = 'signin-error'
  return (
    <div className="min-h-screen bg-bg">
      <div className="stage relative isolate flex min-h-screen flex-col overflow-hidden">
        <header className="border-b border-rule">
          <div className="mx-auto flex w-full max-w-page items-center px-4 py-2.5 min-[481px]:px-6">
            <p className="m-0 inline-flex min-h-[44px] items-center gap-2.5 text-[20px] font-medium tracking-[-0.035em]">
              <Mark />
              {copy.wordmark}
            </p>
          </div>
        </header>
        <main className="flex flex-1 flex-col items-center px-4 pb-16 pt-14 min-[481px]:justify-center min-[481px]:pt-6">
          <h1 className="m-0 mb-3 text-center text-[clamp(36px,24px+3vw,60px)] font-medium leading-[1.06] tracking-[-0.06em] [text-wrap:balance]">
            {copy.signIn.heading}
          </h1>
          <div className="mt-8 w-full max-w-[440px]">
            <div className="glass-panel p-6 min-[481px]:p-7">
              {coding ? (
                <>
                  <form onSubmit={submitCode} noValidate>
                    <label className="mb-5 block text-secondary" htmlFor="code">
                      {copy.signIn.sent(session.email)}
                    </label>
                    <input
                      ref={field}
                      id="code"
                      name="code"
                      className="field mb-3 font-mono text-[20px] tracking-[0.3em]"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={20}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      aria-invalid={problem?.field || undefined}
                      aria-describedby={problem ? errorId : undefined}
                    />
                    <Problem id={errorId} problem={problem} />
                    <button className="button w-full" type="submit" aria-disabled={busy || undefined}>
                      {busy ? copy.signIn.signingIn : copy.signIn.signIn}
                    </button>
                  </form>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4">
                    <Resend resendAt={session.resend_at} onClick={again} busy={busy} />
                    <button type="button" className="min-h-[44px] bg-transparent p-0 text-secondary underline decoration-thread/60 underline-offset-4 hover:text-text" onClick={restart}>
                      {copy.signIn.otherEmail}
                    </button>
                  </div>
                </>
              ) : (
                <form onSubmit={submitEmail} noValidate>
                  <label className="mb-5 block text-secondary" htmlFor="email">
                    {copy.signIn.email}
                  </label>
                  <input
                    ref={field}
                    id="email"
                    name="email"
                    className="field mb-3"
                    type="email"
                    required
                    maxLength={254}
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder={copy.signIn.placeholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={problem?.field || undefined}
                    aria-describedby={problem ? errorId : undefined}
                  />
                  <Problem id={errorId} problem={problem} />
                  <button className="button w-full" type="submit" aria-disabled={busy || undefined}>
                    {busy ? copy.signIn.sending : copy.signIn.send}
                  </button>
                </form>
              )}
            </div>
            {!coding && <p className="mx-1 mt-4 text-center text-[15px] leading-[22px] text-secondary">{copy.signIn.firstTime}</p>}
          </div>
        </main>
      </div>
    </div>
  )
}

function Problem({ id, problem }: { id: string; problem: { text: string; field: boolean } | null }) {
  if (!problem) return null
  return (
    <p id={id} className="-mt-1 mb-3 text-[14px] leading-5" role={problem.field ? undefined : 'status'}>
      {problem.text}
    </p>
  )
}

// Send again, counting down to when another code may be asked for.
function Resend({ resendAt, onClick, busy }: { resendAt: string; onClick: () => void; busy: boolean }) {
  const until = Date.parse(resendAt)
  const [left, setLeft] = useState(() => Math.max(0, Math.ceil((until - serverNow()) / 1000)))
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, Math.ceil((until - serverNow()) / 1000)))
    tick()
    const t = setInterval(tick, 1000)
    return () => clearInterval(t)
  }, [until])
  const waiting = left > 0
  return (
    <button
      type="button"
      className="min-h-[44px] bg-transparent p-0 font-medium text-text underline decoration-thread/60 underline-offset-4 aria-disabled:text-secondary aria-disabled:no-underline"
      onClick={waiting || busy ? undefined : onClick}
      aria-disabled={waiting || busy || undefined}
    >
      {waiting ? copy.signIn.againIn(left) : copy.signIn.again}
    </button>
  )
}
